<template>
  <div class="emp-payslip-page">
    <!-- 앱 헤더 -->
    <div class="app-header">
      <div class="header-title">급여명세서</div>
      <div class="header-year-selector">
        <button class="year-nav-btn" @click="changeYear(-1)">
          <v-icon size="18" color="white">mdi-chevron-left</v-icon>
        </button>
        <span class="year-text">{{ selectedYear }}년</span>
        <button class="year-nav-btn" @click="changeYear(1)">
          <v-icon size="18" color="white">mdi-chevron-right</v-icon>
        </button>
      </div>
    </div>

    <div class="page-content">

    <!-- 월별 급여 목록 -->
    <div class="payslip-list" v-if="!isLoading">
      <div 
        v-for="item in payslipHistory" 
        :key="item.ewi_ym"
        class="payslip-item"
        :class="{ 'closed': item.is_closed === 'y' }"
        @click="openPayslipDetail(item)"
      >
        <div class="item-left">
          <div class="month-badge">
            <span class="month">{{ getMonth(item.ewi_ym) }}</span>
            <span class="unit">월</span>
          </div>
          <div class="status-badge" v-if="item.is_closed === 'y'">
            <v-icon size="14" color="white">mdi-check</v-icon>
            확정
          </div>
        </div>
        <div class="item-right">
          <div class="net-pay">
            <span class="label">실수령액</span>
            <span class="amount">{{ formatCurrency(item.ewi_net_pay) }}원</span>
          </div>
          <v-icon size="20" color="#9ca3af">mdi-chevron-right</v-icon>
        </div>
      </div>

      <!-- 데이터 없음 -->
      <div v-if="payslipHistory.length === 0" class="empty-state">
        <v-icon size="48" color="#d1d5db">mdi-file-document-outline</v-icon>
        <p>{{ selectedYear }}년 급여 내역이 없습니다.</p>
      </div>
    </div>

    <!-- 로딩 -->
    <div v-else class="loading-state">
      <v-progress-circular indeterminate color="#059669" size="40" />
      <p>급여 내역을 불러오는 중...</p>
    </div>

    <!-- 상세 보기 다이얼로그 -->
    <v-dialog v-model="showDetail" max-width="450" >
      <v-card class="payslip-detail-card" v-if="selectedPayslip">
        <div class="detail-header">
          <div class="header-title">
            <span class="year-month">{{ selectedYear }}년 {{ getMonth(selectedPayslip.ewi_ym) }}월</span>
            <span class="title">급여명세서</span>
          </div>
          <div class="header-actions">
            <button class="download-btn" @click="downloadPayslip" :disabled="isDownloading">
              <v-icon size="20">mdi-download</v-icon>
            </button>
            <button class="close-btn" @click="showDetail = false">
              <v-icon>mdi-close</v-icon>
            </button>
          </div>
        </div>

        <div class="detail-content" v-if="!isLoadingDetail">
          <!-- 센터장/사회복지사 급여 상세 -->
          <template v-if="detailType === 'sabok' && detailData">
            <!-- 근무 정보 -->
            <div class="detail-section" v-if="detailData.ewi_service_days || detailData.ewi_suga_hours">
              <h3 class="section-title info">
                <v-icon size="18" color="#2563eb">mdi-clock-outline</v-icon>
                근무 정보
              </h3>
              <div class="detail-row" v-if="detailData.ewi_service_days">
                <span class="label">근무일수</span>
                <span class="value">{{ detailData.ewi_service_days }}일</span>
              </div>
              <div class="detail-row" v-if="detailData.ewi_suga_hours">
                <span class="label">근무시간</span>
                <span class="value">{{ detailData.ewi_suga_hours }}시간</span>
              </div>
            </div>

            <!-- 지급 내역 -->
            <div class="detail-section">
              <h3 class="section-title pay">
                <v-icon size="18" color="#059669">mdi-plus-circle</v-icon>
                지급 내역
              </h3>
              <div class="detail-row" v-if="parseInt(detailData.ewi_monthly_pay || 0) > 0">
                <span class="label">월 기본급</span>
                <span class="value">{{ formatCurrency(detailData.ewi_monthly_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_weekly_pay || 0) > 0">
                <span class="label">주휴수당</span>
                <span class="value">{{ formatCurrency(detailData.ewi_weekly_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_annual_pay || 0) > 0">
                <span class="label">연차수당</span>
                <span class="value">{{ formatCurrency(detailData.ewi_annual_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_holiday_pay || 0) > 0">
                <span class="label">휴일수당</span>
                <span class="value">{{ formatCurrency(detailData.ewi_holiday_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_overtime_pay || 0) > 0">
                <span class="label">연장수당</span>
                <span class="value">{{ formatCurrency(detailData.ewi_overtime_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_night_pay || 0) > 0">
                <span class="label">야간수당</span>
                <span class="value">{{ formatCurrency(detailData.ewi_night_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_meal_pay || 0) > 0">
                <span class="label">식대</span>
                <span class="value">{{ formatCurrency(detailData.ewi_meal_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_long_service_pay || 0) > 0">
                <span class="label">장기근속수당</span>
                <span class="value">{{ formatCurrency(detailData.ewi_long_service_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_bosu_edu_pay || 0) > 0">
                <span class="label">보수교육비</span>
                <span class="value">{{ formatCurrency(detailData.ewi_bosu_edu_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_other_pay || 0) > 0">
                <span class="label">기타수당</span>
                <span class="value">{{ formatCurrency(detailData.ewi_other_pay) }}원</span>
              </div>
              <div class="detail-row total">
                <span class="label">총 지급액</span>
                <span class="value">{{ formatCurrency(detailData.ewi_total_pay) }}원</span>
              </div>
            </div>

            <!-- 공제 내역 -->
            <div class="detail-section" v-if="parseInt(detailData.ewi_emp_total_deduction || 0) > 0">
              <h3 class="section-title deduct">
                <v-icon size="18" color="#dc2626">mdi-minus-circle</v-icon>
                공제 내역
              </h3>
              <div class="detail-row" v-if="parseInt(detailData.ewi_emp_yeon || 0) > 0">
                <span class="label">국민연금</span>
                <span class="value deduct">{{ formatCurrency(detailData.ewi_emp_yeon) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_emp_gun || 0) > 0">
                <span class="label">건강보험</span>
                <span class="value deduct">{{ formatCurrency(detailData.ewi_emp_gun) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_emp_jang || 0) > 0">
                <span class="label">장기요양보험</span>
                <span class="value deduct">{{ formatCurrency(detailData.ewi_emp_jang) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_emp_go || 0) > 0">
                <span class="label">고용보험</span>
                <span class="value deduct">{{ formatCurrency(detailData.ewi_emp_go) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_emp_so || 0) > 0">
                <span class="label">소득세</span>
                <span class="value deduct">{{ formatCurrency(detailData.ewi_emp_so) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_emp_ji || 0) > 0">
                <span class="label">지방소득세</span>
                <span class="value deduct">{{ formatCurrency(detailData.ewi_emp_ji) }}원</span>
              </div>
              <div class="detail-row total">
                <span class="label">총 공제액</span>
                <span class="value deduct">{{ formatCurrency(detailData.ewi_emp_total_deduction) }}원</span>
              </div>
            </div>

            <!-- 실수령액 -->
            <div class="net-pay-section">
              <span class="label">실수령액</span>
              <span class="amount">{{ formatCurrency(detailData.ewi_net_pay) }}원</span>
            </div>
          </template>

          <!-- 요양보호사 급여 상세 (hh_emp_sabok_work_info 테이블 데이터 직접 사용) -->
          <template v-else-if="detailType === 'yobo' && detailData">
            <!-- 근무 정보 -->
            <div class="detail-section" v-if="detailData.ewi_service_days || detailData.ewi_suga_hours">
              <h3 class="section-title info">
                <v-icon size="18" color="#2563eb">mdi-clock-outline</v-icon>
                근무 정보
              </h3>
              <div class="detail-row" v-if="detailData.ewi_service_days">
                <span class="label">서비스 횟수</span>
                <span class="value">{{ detailData.ewi_service_days }}회</span>
              </div>
              <div class="detail-row" v-if="detailData.ewi_suga_hours">
                <span class="label">수가시간</span>
                <span class="value">{{ detailData.ewi_suga_hours }}시간</span>
              </div>
              <div class="detail-row" v-if="detailData.ewi_avg_time_pay > 0">
                <span class="label">평균시급</span>
                <span class="value">{{ formatCurrency(detailData.ewi_avg_time_pay) }}원</span>
              </div>
            </div>

            <!-- 지급 내역 -->
            <div class="detail-section">
              <h3 class="section-title pay">
                <v-icon size="18" color="#059669">mdi-plus-circle</v-icon>
                지급 내역
              </h3>
              <div class="detail-row" v-if="parseInt(detailData.ewi_basic_pay || 0) > 0">
                <span class="label">기본급</span>
                <span class="value">{{ formatCurrency(detailData.ewi_basic_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_weekly_pay || 0) > 0">
                <span class="label">주휴수당</span>
                <span class="value">{{ formatCurrency(detailData.ewi_weekly_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_annual_pay || 0) > 0">
                <span class="label">연차수당</span>
                <span class="value">{{ formatCurrency(detailData.ewi_annual_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_meal_pay || 0) > 0">
                <span class="label">식대</span>
                <span class="value">{{ formatCurrency(detailData.ewi_meal_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_long_service_pay || 0) > 0">
                <span class="label">장기근속수당</span>
                <span class="value">{{ formatCurrency(detailData.ewi_long_service_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_bosu_edu_pay || 0) > 0">
                <span class="label">보수교육비</span>
                <span class="value">{{ formatCurrency(detailData.ewi_bosu_edu_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_severe_care_addition || 0) > 0">
                <span class="label">중증수급자가산</span>
                <span class="value">{{ formatCurrency(detailData.ewi_severe_care_addition) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_holiday_pay || 0) > 0">
                <span class="label">휴일수당</span>
                <span class="value">{{ formatCurrency(detailData.ewi_holiday_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_overtime_pay || 0) > 0">
                <span class="label">연장수당</span>
                <span class="value">{{ formatCurrency(detailData.ewi_overtime_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_night_pay || 0) > 0">
                <span class="label">야간수당</span>
                <span class="value">{{ formatCurrency(detailData.ewi_night_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_labor_day_pay || 0) > 0">
                <span class="label">근로자의날수당</span>
                <span class="value">{{ formatCurrency(detailData.ewi_labor_day_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_other_pay || 0) > 0">
                <span class="label">기타수당</span>
                <span class="value">{{ formatCurrency(detailData.ewi_other_pay) }}원</span>
              </div>
              <div class="detail-row total">
                <span class="label">총 지급액</span>
                <span class="value">{{ formatCurrency(detailData.ewi_total_pay) }}원</span>
              </div>
            </div>

            <!-- 공제 내역 -->
            <div class="detail-section" v-if="parseInt(detailData.ewi_emp_total_deduction || 0) > 0">
              <h3 class="section-title deduct">
                <v-icon size="18" color="#dc2626">mdi-minus-circle</v-icon>
                공제 내역
              </h3>
              <div class="detail-row" v-if="parseInt(detailData.ewi_emp_yeon || 0) > 0">
                <span class="label">국민연금</span>
                <span class="value deduct">{{ formatCurrency(detailData.ewi_emp_yeon) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_emp_gun || 0) > 0">
                <span class="label">건강보험</span>
                <span class="value deduct">{{ formatCurrency(detailData.ewi_emp_gun) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_emp_jang || 0) > 0">
                <span class="label">장기요양보험</span>
                <span class="value deduct">{{ formatCurrency(detailData.ewi_emp_jang) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_emp_go || 0) > 0">
                <span class="label">고용보험</span>
                <span class="value deduct">{{ formatCurrency(detailData.ewi_emp_go) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_emp_so || 0) > 0">
                <span class="label">소득세</span>
                <span class="value deduct">{{ formatCurrency(detailData.ewi_emp_so) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_emp_ji || 0) > 0">
                <span class="label">지방소득세</span>
                <span class="value deduct">{{ formatCurrency(detailData.ewi_emp_ji) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_emp_support_deduction || 0) > 0">
                <span class="label">지원공제</span>
                <span class="value deduct">{{ formatCurrency(detailData.ewi_emp_support_deduction) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_emp_bonin_pay || 0) > 0">
                <span class="label">본인부담금공제</span>
                <span class="value deduct">{{ formatCurrency(detailData.ewi_emp_bonin_pay) }}원</span>
              </div>
              <div class="detail-row" v-if="parseInt(detailData.ewi_emp_etc || 0) > 0">
                <span class="label">기타공제</span>
                <span class="value deduct">{{ formatCurrency(detailData.ewi_emp_etc) }}원</span>
              </div>
              <div class="detail-row total">
                <span class="label">총 공제액</span>
                <span class="value deduct">{{ formatCurrency(detailData.ewi_emp_total_deduction) }}원</span>
              </div>
            </div>

            <!-- 실수령액 -->
            <div class="net-pay-section">
              <span class="label">실수령액</span>
              <span class="amount">{{ formatCurrency(detailData.ewi_net_pay) }}원</span>
            </div>

            <div class="yobo-notice" v-if="parseFloat(detailData.ewi_suga_hours || 0) < 60">
              <v-icon size="16" color="#d97706">mdi-alert-circle-outline</v-icon>
              <span>60시간 미만 근무로 4대보험 공제가 적용되지 않습니다.</span>
            </div>
          </template>

          <!-- 데이터 없음 -->
          <div v-else class="no-data-section">
            <v-icon size="48" color="#d1d5db">mdi-file-document-outline</v-icon>
            <p>해당 월 급여 상세 정보가 없습니다.</p>
          </div>
        </div>

        <!-- 상세 로딩 -->
        <div v-else class="detail-loading">
          <v-progress-circular indeterminate color="#059669" size="32" />
          <p>상세 정보를 불러오는 중...</p>
        </div>
      </v-card>
    </v-dialog>
    </div>

    <!-- 연도 선택 다이얼로그 -->
    <v-dialog v-model="showYearPicker" max-width="300">
      <v-card rounded="lg">
        <v-card-title class="dialog-title">연도 선택</v-card-title>
        <v-card-text class="dialog-content">
          <div class="year-list">
            <button
              v-for="year in availableYears"
              :key="year"
              class="year-item"
              :class="{ active: selectedYear === year }"
              @click="selectYear(year)"
            >
              <span>{{ year }}년</span>
              <v-icon v-if="selectedYear === year" size="20" color="#059669">mdi-check</v-icon>
            </button>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';

const store = useStore();

// 사원 정보
const pwaUser = computed(() => store.getters['pwa/pwaUser']);
const empIdx = computed(() => pwaUser.value?.emp_idx || '');
const sisulCode = computed(() => pwaUser.value?.m_sisul_code || '');
const empBirth = computed(() => pwaUser.value?.emp_birth || '');
const empWorkStartDate = computed(() => pwaUser.value?.emp_in_date || pwaUser.value?.lem_work_start_date || '');

// 상태
const isLoading = ref(false);
const isLoadingDetail = ref(false);
const isDownloading = ref(false);
const selectedYear = ref(new Date().getFullYear());
const payslipHistory = ref<any[]>([]);
const empType = ref<'sabok' | 'yobo'>('yobo');
const showYearPicker = ref(false);
const showDetail = ref(false);
const selectedPayslip = ref<any>(null);
const detailType = ref<'sabok' | 'yobo' | null>(null);
const detailData = ref<any>(null);
const taxData = ref<any>(null);

// 연도 목록 (현재 연도 기준 5년)
const availableYears = computed(() => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 5 }, (_, i) => currentYear - i);
});

// 원단위 절삭 함수 (예: 51131 -> 51130)
const truncateToTen = (value: number) => Math.floor(value / 10) * 10;

// 만 나이 계산
const calculateAge = (birthDateStr: string, baseYm: string) => {
  if (!birthDateStr || !baseYm) return 0;
  // YYYYMMDD -> YYYY-MM-DD 변환
  let birthFormatted = birthDateStr;
  if (/^\d{8}$/.test(birthDateStr)) {
    birthFormatted = `${birthDateStr.substring(0, 4)}-${birthDateStr.substring(4, 6)}-${birthDateStr.substring(6, 8)}`;
  }
  const birthDate = new Date(birthFormatted);
  const baseYear = parseInt(baseYm.substring(0, 4));
  const baseMonth = parseInt(baseYm.substring(4, 6));
  const baseDate = new Date(baseYear, baseMonth, 0);
  let age = baseDate.getFullYear() - birthDate.getFullYear();
  if (baseDate.getMonth() < birthDate.getMonth() ||
    (baseDate.getMonth() === birthDate.getMonth() && baseDate.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// 근속년수 계산 (연차일수 계산용)
const calculateWorkPeriod = (startDateStr: string, baseYm: string) => {
  if (!startDateStr || !baseYm) return { years: 0, months: 0 };
  // YYYYMMDD -> YYYY-MM-DD 변환
  let startFormatted = startDateStr;
  if (/^\d{8}$/.test(startDateStr)) {
    startFormatted = `${startDateStr.substring(0, 4)}-${startDateStr.substring(4, 6)}-${startDateStr.substring(6, 8)}`;
  }
  const workStartDate = new Date(startFormatted);
  const baseYear = parseInt(baseYm.substring(0, 4));
  const baseMonth = parseInt(baseYm.substring(4, 6));
  const baseDate = new Date(baseYear, baseMonth - 1, 1);
  let years = baseDate.getFullYear() - workStartDate.getFullYear();
  let months = baseDate.getMonth() - workStartDate.getMonth();
  if (months < 0) {
    years--;
    months += 12;
  }
  return { years, months };
};

// 연차 일수 계산 (근속년수 기반)
const getAnnualLeaveDays = (startDateStr: string, baseYm: string) => {
  if (!startDateStr || !baseYm) return 11;
  const period = calculateWorkPeriod(startDateStr, baseYm);
  if (period.years === 0 && period.months === 0) return 0;
  const yearLevel = period.years + 1;
  if (yearLevel >= 21) return 25;
  
  const vacationYearOptions = [
    { value: 1, days: 11 }, { value: 2, days: 15 }, { value: 3, days: 15 },
    { value: 4, days: 16 }, { value: 5, days: 16 }, { value: 6, days: 17 },
    { value: 7, days: 17 }, { value: 8, days: 18 }, { value: 9, days: 18 },
    { value: 10, days: 19 }, { value: 11, days: 19 }, { value: 12, days: 20 },
    { value: 13, days: 20 }, { value: 14, days: 21 }, { value: 15, days: 21 },
    { value: 16, days: 22 }, { value: 17, days: 22 }, { value: 18, days: 23 },
    { value: 19, days: 23 }, { value: 20, days: 24 }
  ];
  
  const option = vacationYearOptions.find(opt => opt.value === yearLevel);
  return option ? option.days : 11;
};

// 그룹화된 수급자 데이터
const groupedPatients = computed(() => {
  if (detailType.value !== 'yobo' || !Array.isArray(detailData.value)) return [];
  
  return detailData.value.map((item: any) => ({
    patientName: item.leb_patient_name || item.patient_name || '수급자',
    patientCode: item.leb_patient_code || item.lemi_patient_code || '',
    sugaCode: item.suga_code || item.lemi_suga_code || '',
    sugaName: item.suga_name || '-',
    timePay: parseInt(String(item.time_pay || item.lemi_time_pay || '0').replace(/[^\d]/g, '')) || 0,
    serviceDays: parseInt(item.service_days) || 0,
    sugaHours: parseFloat(item.suga_hours) || 0,
    totalSugaMoney: parseInt(String(item.total_suga_money || '0').replace(/[^\d]/g, '')) || 0,
    lunchTimeCount: parseInt(item.lunch_time_count) || 0
  }));
});

// 가족요양만 있는지 확인
const isFamilyOnly = computed(() => {
  return groupedPatients.value.every((p: any) => (p.sugaName || '').includes('가족'));
});

// 총 점심 횟수
const totalLunchTimeCount = computed(() => {
  return groupedPatients.value.reduce((sum: number, p: any) => sum + (p.lunchTimeCount || 0), 0);
});

// 중증수급자가산 총액 계산
const totalSevereCareAddition = computed(() => {
  if (!Array.isArray(detailData.value)) return 0;
  return detailData.value
    .filter((item: any) => (item.suga_name || '').includes('중증수급자'))
    .reduce((sum: number, item: any) => {
      return sum + (parseInt(String(item.total_suga_money || '0').replace(/[^\d]/g, '')) || 0);
    }, 0);
});

// 요양보호사 급여 계산 (웹 StepThree.vue와 동일한 로직)
const yoboPayroll = computed(() => {
  if (detailType.value !== 'yobo' || !Array.isArray(detailData.value) || detailData.value.length === 0) {
    return null;
  }

  const patients = groupedPatients.value;
  const firstItem = detailData.value[0];
  
  // 설정 데이터
  const settings = {
    applyHealthInsurance: firstItem.lemi_apply_gun === 'Y',
    applyEmploymentInsurance: firstItem.lemi_apply_go === 'Y',
    applyHoliday: firstItem.lemi_apply_holiday_pay === 'Y',
    applyOvertime: firstItem.lemi_apply_overtime_pay === 'Y',
    applyMeal: firstItem.lemi_apply_lunch_pay === 'Y',
    applyRetirement: firstItem.lemi_apply_out_pay === 'Y',
    longServicePay: parseInt(String(firstItem.lemi_long_service_pay || '0').replace(/[^\d]/g, '')) || 0,
    bosuEduPay: parseInt(String(firstItem.lemi_bosu_edu_pay || '0').replace(/[^\d]/g, '')) || 0,
    yeonPay: parseInt(String(firstItem.lemi_yeon_pay || '0').replace(/[^\d]/g, '')) || 0,
    boninPay: parseInt(String(firstItem.lemi_bonin_pay || '0').replace(/[^\d]/g, '')) || 0,
    empEtc: parseInt(String(firstItem.lemi_emp_etc || '0').replace(/[^\d]/g, '')) || 0,
    isBoss: firstItem.lem_isboss === 'Y',
    workStartDate: firstItem.lem_work_start_date || empWorkStartDate.value || ''
  };

  // 총 수가시간, 서비스 횟수 계산
  let totalSugaHours = 0;
  let serviceDays = 0;
  patients.forEach((patient: any) => {
    totalSugaHours += (patient.serviceDays || 0) * (patient.sugaHours || 0);
    serviceDays += patient.serviceDays || 0;
  });

  // 평균 시급 계산 (가중평균)
  let totalWeightedPay = 0;
  let totalHours = 0;
  
  patients.forEach((patient: any) => {
    const timePay = patient.timePay || 0;
    if (timePay > 0 && patient.sugaHours > 0) {
      const sugaHours = (patient.serviceDays || 0) * (patient.sugaHours || 0);
      totalWeightedPay += timePay * sugaHours;
      totalHours += sugaHours;
    }
  });

  const avgTimePay = totalHours > 0 ? Math.round(totalWeightedPay / totalHours) : 0;
  const totalMonthlyPay = totalWeightedPay;
  const longServicePay = settings.longServicePay || 0;
  const bosuEduPay = settings.bosuEduPay || 0;
  const severeCareAddition = totalSevereCareAddition.value;
  
  // 수당 계산 (60시간 이상일 때만)
  const canApplyAllowances = totalSugaHours >= 60;
  const holidayAllowance = settings.applyHoliday && canApplyAllowances ? 
    Math.min(Math.round(avgTimePay * 8 * 1.5), 200000) : 0;
  const overtimeAllowance = settings.applyOvertime && canApplyAllowances ? 
    Math.min(Math.round(avgTimePay * 4 * 1.5), 200000) : 0;
  
  // 기본급 등 계산
  let basicPay = 0;
  let holidayPay = 0;
  let vacationPay = 0;
  let actualMealAllowance = 0;
  
  const baseYm = selectedPayslip.value?.ewi_ym || '';
  
  if (isFamilyOnly.value) {
    basicPay = totalMonthlyPay;
  } else {
    const minWage = taxData.value?.tr_time_pay || 9860;
    if (minWage > 0) {
      basicPay = minWage * totalSugaHours;
      holidayPay = Math.round(basicPay * 0.2);
      
      // 연차일수 계산 (근속년수 기반)
      const annualLeaveDays = getAnnualLeaveDays(settings.workStartDate, baseYm);
      vacationPay = serviceDays > 0 ? Math.round((minWage * totalSugaHours / serviceDays) * annualLeaveDays / 12) : 0;
      
      // 식대 계산
      if (settings.applyMeal && totalLunchTimeCount.value > 0) {
        const requestedMealAllowance = Math.min(totalLunchTimeCount.value * 20000, 200000);
        const currentBasicTotal = basicPay + holidayPay + vacationPay;
        const remainingAmount = Math.max(0, totalMonthlyPay - currentBasicTotal);
        const availableAmount = Math.min(requestedMealAllowance, remainingAmount);
        const roundedAmount = Math.floor(availableAmount / 100) * 100;
        if (roundedAmount >= 5000) {
          actualMealAllowance = roundedAmount;
        }
      }
      
      const currentTotal = basicPay + holidayPay + vacationPay + actualMealAllowance;
      if (currentTotal < totalMonthlyPay) {
        vacationPay += (totalMonthlyPay - currentTotal);
      }
    } else {
      basicPay = totalMonthlyPay;
    }
  }
  
  const basicTotal = isFamilyOnly.value ? totalMonthlyPay : (basicPay + holidayPay + vacationPay + actualMealAllowance);
  const totalPay = basicTotal + longServicePay + bosuEduPay + severeCareAddition + holidayAllowance + overtimeAllowance;
  
  // 비과세 처리
  const isSalaryUnder2_1M = basicTotal <= 2100000;
  
  let nonTaxableHoliday = 0;
  let nonTaxableOvertime = 0;
  
  if (canApplyAllowances && isSalaryUnder2_1M) {
    const totalOvertimeHolidayAllowance = holidayAllowance + overtimeAllowance;
    
    if (totalOvertimeHolidayAllowance <= 200000) {
      nonTaxableHoliday = holidayAllowance;
      nonTaxableOvertime = overtimeAllowance;
    } else {
      const nonTaxableRatio = 200000 / totalOvertimeHolidayAllowance;
      nonTaxableHoliday = Math.round(holidayAllowance * nonTaxableRatio);
      nonTaxableOvertime = Math.round(overtimeAllowance * nonTaxableRatio);
    }
  }
  
  const nonTaxableMeal = Math.min(actualMealAllowance, 200000);
  const taxableHoliday = holidayAllowance - nonTaxableHoliday;
  const taxableOvertime = overtimeAllowance - nonTaxableOvertime;
  const taxableAmount = basicTotal + longServicePay + bosuEduPay + taxableHoliday + taxableOvertime - actualMealAllowance + (actualMealAllowance - nonTaxableMeal);
  
  // 보험 요율
  const insuranceRates = {
    nationalPension: taxData.value?.tr_yeon || 0.045,
    healthInsurance: taxData.value?.tr_gun || 0.03545,
    longTermCare: taxData.value?.tr_jang || 0.1295,
    employmentInsurance: taxData.value?.tr_go || 0.009
  };

  const age = calculateAge(empBirth.value, baseYm);
  
  const yeonPayAmount = settings.yeonPay || 0;
  const canApplyPension = age < 60 && yeonPayAmount > 0;
  const canApplyEmployment = age < 65 && settings.applyEmploymentInsurance && !settings.isBoss;

  // 근로자 공제 (원단위 절삭 적용)
  const employeeDeductions = {
    nationalPension: canApplyPension ? truncateToTen(yeonPayAmount) : 0,
    healthInsurance: settings.applyHealthInsurance && canApplyAllowances ? truncateToTen(taxableAmount * insuranceRates.healthInsurance) : 0,
    longTermCare: 0,
    employmentInsurance: canApplyEmployment && canApplyAllowances ? truncateToTen(taxableAmount * insuranceRates.employmentInsurance) : 0,
    incomeTax: canApplyAllowances ? truncateToTen(taxableAmount * 0.03) : 0,
    localTax: 0
  };
  
  employeeDeductions.longTermCare = truncateToTen(employeeDeductions.healthInsurance * insuranceRates.longTermCare);
  employeeDeductions.localTax = truncateToTen(employeeDeductions.incomeTax * 0.1);

  // 추가 공제
  const boninPayAmount = settings.boninPay || 0;
  const empEtcAmount = settings.empEtc || 0;

  const totalEmployeeDeduction = 
    employeeDeductions.nationalPension + employeeDeductions.healthInsurance +
    employeeDeductions.longTermCare + employeeDeductions.employmentInsurance +
    employeeDeductions.incomeTax + employeeDeductions.localTax +
    boninPayAmount + empEtcAmount;

  const netPay = totalPay - totalEmployeeDeduction;

  return {
    basicPay,
    holidayPay,
    vacationPay,
    mealAllowance: actualMealAllowance,
    basicTotal,
    longServicePay,
    bosuEduPay,
    severeCareAddition,
    holidayAllowance,
    overtimeAllowance,
    totalPay,
    employeeDeductions,
    boninPayAmount,
    empEtcAmount,
    totalEmployeeDeduction,
    netPay,
    suga_hours: Math.round(totalSugaHours * 10) / 10,
    service_days: serviceDays
  };
});

// 연도 변경
const changeYear = (delta: number) => {
  const newYear = selectedYear.value + delta;
  const currentYear = new Date().getFullYear();
  if (newYear >= currentYear - 4 && newYear <= currentYear) {
    selectedYear.value = newYear;
  }
};

// 연도 선택
const selectYear = (year: number) => {
  selectedYear.value = year;
  showYearPicker.value = false;
};

// 월 추출
const getMonth = (ym: string): number => {
  if (!ym) return 0;
  return parseInt(ym.substring(4, 6), 10);
};

// 금액 포맷
const formatCurrency = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined) return '0';
  const numValue = typeof value === 'string' ? parseInt(value.replace(/[^\d]/g, ''), 10) : value;
  if (isNaN(numValue)) return '0';
  return numValue.toLocaleString('ko-KR');
};

// 세금 데이터 조회
const fetchTaxData = async (year: string) => {
  try {
    const response = await axios.get(`/api/calc/tax/${year}`, { withCredentials: true });
    if (response.data.success) {
      taxData.value = response.data.data.taxRate;
    }
  } catch (error) {
    console.error('세금 데이터 조회 오류:', error);
  }
};

// 급여 내역 조회
const fetchPayslipHistory = async () => {
  if (!empIdx.value || !sisulCode.value) {
    console.warn('사원 정보가 없습니다.');
    return;
  }

  isLoading.value = true;
  try {
    const response = await axios.get(
      `/api/emp/payslip-history/${empIdx.value}/${sisulCode.value}/${selectedYear.value}`,
      { withCredentials: true }
    );

    if (response.data.success) {
      // 마감(확정)된 데이터만 필터링하여 표시
      const allData = response.data.data || [];
      payslipHistory.value = allData.filter((item: any) => item.is_closed === 'y');
      empType.value = response.data.type || 'yobo';
    } else {
      payslipHistory.value = [];
    }
  } catch (error) {
    console.error('급여 내역 조회 오류:', error);
    payslipHistory.value = [];
  } finally {
    isLoading.value = false;
  }
};

// 급여 상세 조회
const openPayslipDetail = async (item: any) => {
  selectedPayslip.value = item;
  showDetail.value = true;
  isLoadingDetail.value = true;
  detailData.value = null;
  detailType.value = null;

  // 세금 데이터 먼저 조회
  const year = item.ewi_ym.substring(0, 4);
  await fetchTaxData(year);

  try {
    const response = await axios.get(
      `/api/emp/payslip/${empIdx.value}/${sisulCode.value}/${item.ewi_ym}`,
      { withCredentials: true }
    );

    if (response.data.success) {
      detailType.value = response.data.type;
      detailData.value = response.data.data;
    } else {
      store.commit('showSnackbar', {
        text: response.data.message || '급여 상세 정보를 불러올 수 없습니다.',
        color: 'error'
      });
    }
  } catch (error) {
    console.error('급여 상세 조회 오류:', error);
    store.commit('showSnackbar', {
      text: '급여 상세 정보 조회 중 오류가 발생했습니다.',
      color: 'error'
    });
  } finally {
    isLoadingDetail.value = false;
  }
};

// 급여명세서 다운로드
const downloadPayslip = async () => {
  if (isDownloading.value || !selectedPayslip.value) return;
  
  isDownloading.value = true;
  try {
    const yearMonth = selectedPayslip.value.ewi_ym;
    const empName = pwaUser.value?.emp_name || '사원';
    
    store.commit('showSnackbar', {
      text: '급여명세서 다운로드 기능은 준비 중입니다.',
      color: 'info'
    });
  } catch (error) {
    console.error('급여명세서 다운로드 오류:', error);
    store.commit('showSnackbar', {
      text: '급여명세서 다운로드 중 오류가 발생했습니다.',
      color: 'error'
    });
  } finally {
    isDownloading.value = false;
  }
};

// 연도 변경 시 데이터 재조회
watch(selectedYear, () => {
  fetchPayslipHistory();
});

onMounted(() => {
  fetchPayslipHistory();
});
</script>

<style scoped lang="scss">
.emp-payslip-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: white;
  position: sticky;
  top: 0;
  z-index: 100;
  
  .header-title {
    font-size: 17px;
    font-weight: 600;
  }
  
  .header-year-selector {
    display: flex;
    align-items: center;
    gap: 4px;
    
    .year-nav-btn {
      width: 28px;
      height: 28px;
      border: none;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      
      &:active {
        background: rgba(255, 255, 255, 0.3);
      }
    }
    
    .year-text {
      font-size: 15px;
      font-weight: 600;
      padding: 4px 8px;
    }
  }
}

.page-content {
  padding: 16px;
  padding-bottom: 80px;
}

.payslip-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payslip-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:active {
    transform: scale(0.98);
  }
  
  &.closed {
    border-left: 4px solid #059669;
  }
}

.item-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.month-badge {
  display: flex;
  align-items: baseline;
  gap: 2px;
  
  .month {
    font-size: 24px;
    font-weight: 700;
    color: #059669;
  }
  
  .unit {
    font-size: 14px;
    font-weight: 500;
    color: #6b7280;
  }
}

.status-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #059669;
  color: white;
  font-size: 11px;
  font-weight: 600;
  border-radius: 6px;
}

.item-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.net-pay {
  text-align: right;
  
  .label {
    display: block;
    font-size: 12px;
    color: #6b7280;
  }
  
  .amount {
    display: block;
    font-size: 16px;
    font-weight: 700;
    color: #1f2937;
  }
}

.empty-state,
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 12px;
  
  p {
    font-size: 14px;
    color: #9ca3af;
  }
}

.payslip-detail-card {
  border-radius: 16px !important;
  overflow: hidden;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
}

.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: white;
}

.header-title {
  display: flex;
  flex-direction: column;
  
  .year-month {
    font-size: 13px;
    opacity: 0.9;
  }
  
  .title {
    font-size: 18px;
    font-weight: 700;
  }
}

.header-actions {
  display: flex;
  gap: 8px;
}

.download-btn,
.close-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  
  &:active {
    background: rgba(255, 255, 255, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.detail-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.detail-section {
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
  
  &.info {
    color: #2563eb;
  }
  
  &.pay {
    color: #059669;
  }
  
  &.deduct {
    color: #dc2626;
  }
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  
  .label {
    font-size: 14px;
    color: #6b7280;
  }
  
  .value {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    
    &.deduct {
      color: #dc2626;
    }
  }
  
  &.total {
    padding-top: 12px;
    margin-top: 4px;
    border-top: 1px dashed #e5e7eb;
    
    .label {
      font-weight: 600;
      color: #1f2937;
    }
    
    .value {
      font-size: 16px;
    }
  }
}

.net-pay-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  border-radius: 12px;
  margin: 16px 0;
  
  .label {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.9);
  }
  
  .amount {
    font-size: 22px;
    font-weight: 700;
    color: white;
  }
}

.yobo-service-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  
  /* 스크롤바 항상 표시 */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    
    &:hover {
      background: #94a3b8;
    }
  }
}

.yobo-service-item {
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
}

.service-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  
  .patient-name {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
  }
  
  .suga-name {
    font-size: 12px;
    color: #6b7280;
    background: #e5e7eb;
    padding: 2px 8px;
    border-radius: 4px;
  }
}

.service-detail {
  display: flex;
  gap: 16px;
  
  .detail-col {
    flex: 1;
    
    .label {
      display: block;
      font-size: 11px;
      color: #9ca3af;
      margin-bottom: 2px;
    }
    
    .value {
      font-size: 13px;
      font-weight: 600;
      color: #374151;
    }
  }
}

.yobo-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #fef3c7;
  border-radius: 8px;
  font-size: 13px;
  color: #92400e;
}

.no-data-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 12px;
  
  p {
    font-size: 14px;
    color: #9ca3af;
  }
}

.detail-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
  
  p {
    font-size: 14px;
    color: #6b7280;
  }
}

.dialog-title {
  font-size: 16px !important;
  font-weight: 600 !important;
  text-align: center;
  padding: 16px !important;
}

.dialog-content {
  padding: 0 16px 16px !important;
}

.year-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.year-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: #f3f4f6;
  border-radius: 8px;
  font-size: 15px;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &.active {
    background: #d1fae5;
    color: #059669;
    font-weight: 600;
  }
  
  &:active {
    transform: scale(0.98);
  }
}
</style>
